import {
  useFieldArray,
  type UseFieldArrayProps,
  type UseFormReturn,
} from "react-hook-form";
import {
  Input,
  FieldError,
  Label,
  Legend,
  RepeaterFields,
  RepeaterField,
  TextArea,
  Fieldset,
  RequiredText,
  AppError,
} from "atoms";
import { ImageUpload } from "features/images";
import { SelectReviewableLinkType } from "features/misc";
import { fieldOptions, validators } from "utils/form";
import type { ArtistFormFields } from "../types";

type ArtistCollectionInputProps = Pick<
  UseFormReturn<ArtistFormFields>,
  "register" | "setValue"
> &
  Pick<UseFieldArrayProps<ArtistFormFields>, "control"> & {
    errors?: UseFormReturn<ArtistFormFields>["formState"]["errors"];
    maxCollections: number;
  };

function ArtistCollectionsInput({
  control,
  errors,
  register,
  setValue,
  maxCollections,
  ...props
}: ArtistCollectionInputProps) {
  const { fields, append, move, remove } = useFieldArray({
    control,
    name: "artistCollections",
  });

  const handleAppend = () =>
    append({ collectionName: "", collectionLinks: [] });

  // Do not allow append if over the max
  const showAppend = fields.length < maxCollections;

  return (
    <Label as="fieldset" {...props}>
      <Legend>Collections</Legend>

      <RepeaterFields
        handleAppend={handleAppend}
        rowLabel="Collection"
        showAppend={showAppend}
      >
        {fields.map((item, index) => (
          <RepeaterField
            key={item.id}
            order={index}
            rowLabel="Collection"
            {...RepeaterField.createHandlers(
              index,
              fields.length,
              move,
              remove
            )}
          >
            <Label>
              Collection Name
              <RequiredText />
              <Input
                {...register(`artistCollections.${index}.collectionName`, {
                  ...fieldOptions.required,
                })}
                isInvalid={!!errors?.artistCollections?.[index]?.collectionName}
              />
              {errors?.artistCollections?.[index]?.collectionName && (
                <FieldError>
                  {errors?.artistCollections?.[index]?.collectionName?.message}
                </FieldError>
              )}
            </Label>

            <fieldset>
              <Legend>
                Thumbnail
                <RequiredText />
              </Legend>

              <ImageUpload
                register={register}
                setValue={setValue}
                fieldName={`artistCollections.${index}.collectionImage`}
                required
                aspect="square"
                entityType="PROJECT"
                errorMessage={
                  errors?.artistCollections?.[index]?.collectionImage?.url
                    ?.message
                }
                src={item.collectionImage}
              />
            </fieldset>

            <Label>
              Description
              <br />
              <small>
                Only the first 90-110 characters will be displayed on your
                profile.
              </small>
              <TextArea
                {...register(
                  `artistCollections.${index}.collectionDescription`
                )}
              />
            </Label>

            <Fieldset {...props}>
              <Legend>Primary marketplace</Legend>

              <Label>
                <p>Marketplace</p>
                <SelectReviewableLinkType
                  marketplacesOnly
                  {...register(
                    `artistCollections.${index}.collectionLinks.0.type`
                  )}
                />
              </Label>

              <Label>
                <p>URL</p>
                <Input
                  {...register(
                    `artistCollections.${index}.collectionLinks.0.url`,
                    {
                      validate: validators.validURL,
                    }
                  )}
                />
                {errors?.artistCollections?.[index]?.collectionLinks?.[0]
                  ?.url && (
                  <FieldError block>
                    {
                      errors?.artistCollections?.[index]?.collectionLinks?.[0]
                        ?.url?.message
                    }
                  </FieldError>
                )}
              </Label>
            </Fieldset>
          </RepeaterField>
        ))}
      </RepeaterFields>

      {!showAppend && (
        <AppError>
          You have reached the maximum number of permitted collections.
        </AppError>
      )}
    </Label>
  );
}

export { ArtistCollectionsInput };
